/**
 * Display formatting helpers.
 *
 * The API serves ISO 8601 UTC timestamps and sizes in bytes (`docs/API.md`);
 * everything shown to a GTM person is a short English phrase. Nothing here
 * computes a fact: a number the console did not measure is `null`, and these
 * functions render that as an em dash rather than inventing a zero.
 */

const MINUTE_MS = 60_000;
const HOUR_MS = 3_600_000;
const DAY_MS = 86_400_000;

/** What a missing measurement looks like. Never a 0, never a "—" with a unit. */
export const UNKNOWN = "—";

/**
 * Format a byte count the way a person says it.
 *
 * Decimal units, because that is what a file manager and a client's e-mail
 * both say: 204.8 MB is the number the trace prints for 214 748 364 bytes.
 *
 * Args:
 *     bytes: A measured size, or null when there is none.
 *
 * Returns:
 *     e.g. `1.9 GB`, `204.8 MB`, `812 kB`, or `—` when unknown.
 */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) {
    return UNKNOWN;
  }
  if (bytes < 1_000) {
    return `${Math.round(bytes)} B`;
  }
  const units = ["kB", "MB", "GB", "TB", "PB"];
  let value = bytes / 1_000;
  let unit = 0;
  while (value >= 1_000 && unit < units.length - 1) {
    value /= 1_000;
    unit += 1;
  }
  // One decimal, always: the trace says "204.8 MB" and the screen beside it
  // must say the same thing rather than a rounder version of it.
  const rounded = Math.round(value * 10) / 10;
  return `${rounded} ${units[unit]}`;
}

/**
 * Format a count of rows or columns with thin spaces between the thousands.
 *
 * Args:
 *     count: A measured count, or null when the console could not take it.
 *
 * Returns:
 *     e.g. `1 199 998`, or `—` when unknown.
 */
export function formatCount(count: number | null | undefined): string {
  if (count === null || count === undefined || !Number.isFinite(count)) {
    return UNKNOWN;
  }
  // Ordinary spaces between the thousands, not the narrow no-break space
  // some locales group with: this number is read out loud, copied into a
  // mail and compared with what a client said, and it must survive all three.
  return new Intl.NumberFormat("en-GB", { useGrouping: true })
    .format(count)
    .replace(/[,\u202f\u00a0]/g, " ");
}

/**
 * Format a duration in the console's house style.
 *
 * Args:
 *     milliseconds: Duration to format; negative values are clamped to zero.
 *
 * Returns:
 *     e.g. `45 s`, `12 min`, `1 h 04`, `3 d 04 h`.
 */
export function formatDuration(milliseconds: number): string {
  const value = Math.max(0, Math.round(milliseconds));
  if (value < MINUTE_MS) {
    return `${Math.floor(value / 1000)} s`;
  }
  if (value < HOUR_MS) {
    return `${Math.floor(value / MINUTE_MS)} min`;
  }
  if (value < DAY_MS) {
    const hours = Math.floor(value / HOUR_MS);
    const minutes = Math.floor((value % HOUR_MS) / MINUTE_MS);
    return minutes === 0
      ? `${hours} h`
      : `${hours} h ${String(minutes).padStart(2, "0")}`;
  }
  const days = Math.floor(value / DAY_MS);
  const hours = Math.floor((value % DAY_MS) / HOUR_MS);
  return hours === 0
    ? `${days} d`
    : `${days} d ${String(hours).padStart(2, "0")} h`;
}

/**
 * Format a number of seconds the server published as a delay.
 *
 * Args:
 *     seconds: A measured delay, or null when it could not be measured.
 *
 * Returns:
 *     e.g. `6 min`, or `—` when unknown.
 */
export function formatSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) {
    return UNKNOWN;
  }
  return formatDuration(seconds * 1000);
}

/**
 * Milliseconds remaining until an ISO timestamp.
 *
 * Args:
 *     iso: ISO 8601 timestamp, or null.
 *     now: Reference instant in milliseconds (injectable for tests).
 *
 * Returns:
 *     Milliseconds remaining (negative when past), or null when unparseable.
 */
export function remainingUntil(
  iso: string | null | undefined,
  now: number = Date.now(),
): number | null {
  if (!iso) {
    return null;
  }
  const timestamp = Date.parse(iso);
  return Number.isNaN(timestamp) ? null : timestamp - now;
}

/**
 * Format the time left before a deadline.
 *
 * Args:
 *     iso: ISO 8601 timestamp, or null.
 *     now: Reference instant in milliseconds.
 *
 * Returns:
 *     e.g. `5 h 20`, `now` when the deadline has passed, `—` when unknown.
 */
export function formatRemaining(
  iso: string | null | undefined,
  now: number = Date.now(),
): string {
  const remaining = remainingUntil(iso, now);
  if (remaining === null) {
    return UNKNOWN;
  }
  return remaining <= 0 ? "now" : formatDuration(remaining);
}

/**
 * Format a wall-clock time, e.g. `18:40`.
 *
 * Args:
 *     iso: ISO 8601 timestamp, or null.
 *
 * Returns:
 *     The local time, or `—` when unknown.
 */
export function formatClock(iso: string | null | undefined): string {
  if (!iso) {
    return UNKNOWN;
  }
  const timestamp = Date.parse(iso);
  if (Number.isNaN(timestamp)) {
    return UNKNOWN;
  }
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(timestamp));
}

/**
 * Format an absolute date and time, for traces and ledgers.
 *
 * Args:
 *     iso: ISO 8601 timestamp, or null.
 *
 * Returns:
 *     e.g. `12 Aug 2026, 14:03`, or `—` when unknown.
 */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) {
    return UNKNOWN;
  }
  const timestamp = Date.parse(iso);
  if (Number.isNaN(timestamp)) {
    return UNKNOWN;
  }
  return new Intl.DateTimeFormat("en-GB", {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(timestamp));
}

/**
 * Format a date without its time, for a consent or a receipt.
 *
 * Args:
 *     iso: ISO 8601 timestamp, or null.
 *
 * Returns:
 *     e.g. `12 Aug 2026`, or `—` when unknown.
 */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) {
    return UNKNOWN;
  }
  const timestamp = Date.parse(iso);
  if (Number.isNaN(timestamp)) {
    return UNKNOWN;
  }
  return new Intl.DateTimeFormat("en-GB", { dateStyle: "medium" }).format(
    new Date(timestamp),
  );
}

/**
 * Format a measured score without pretending to a precision it lacks.
 *
 * Args:
 *     value: A number somebody reported, or null.
 *
 * Returns:
 *     e.g. `0.847`, `12.5`, or `—` when there is none.
 */
export function formatScore(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return UNKNOWN;
  }
  const rounded = Math.round(value * 1000) / 1000;
  return String(rounded);
}

/**
 * Format the distance between a result and the bar, with its sign.
 *
 * Args:
 *     margin: The signed margin the server computed, or null.
 *
 * Returns:
 *     e.g. `+0.027`, `-0.004`, or `—` when there is none.
 */
export function formatMargin(margin: number | null | undefined): string {
  if (margin === null || margin === undefined || !Number.isFinite(margin)) {
    return UNKNOWN;
  }
  const rounded = Math.round(margin * 1000) / 1000;
  return rounded >= 0 ? `+${rounded}` : String(rounded);
}

/**
 * Pluralize a count with its unit.
 *
 * Args:
 *     count: The number.
 *     singular: Unit in the singular.
 *     plural: Unit in the plural; defaults to `singular + "s"`.
 *
 * Returns:
 *     e.g. `1 version`, `3 versions`.
 */
export function pluralize(
  count: number,
  singular: string,
  plural = `${singular}s`,
): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

/**
 * Join a list the way a sentence does.
 *
 * Args:
 *     items: The words to join.
 *
 * Returns:
 *     e.g. `benchmark and training`, `benchmark, training and exploration`.
 */
export function joinWords(items: readonly string[]): string {
  if (items.length === 0) {
    return "";
  }
  if (items.length === 1) {
    return items[0]!;
  }
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]!}`;
}

/**
 * Turn a name into the short name a path segment can carry.
 *
 * Offered as a prefilled suggestion only: the short name is frozen at
 * creation, so a person sees it and may change it before it becomes part of
 * every object key their client's files are ever written under.
 *
 * Args:
 *     name: What a person typed.
 *
 * Returns:
 *     Lower-case, `[a-z0-9-]`, at most 40 characters.
 */
export function toSlug(name: string): string {
  return name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 40)
    .replace(/-+$/g, "");
}

/**
 * Read a number a person typed, or say it is not one.
 *
 * `Number("0,82")` is `NaN`, and `JSON.stringify` turns `NaN` into `null` — so
 * a French keyboard's comma becomes a null the client never meant to send, and
 * the server answers a refusal that names no field. A comma is normalised
 * because that is what actually fixes it; anything else that is not a finite
 * number comes back as `null`, so the screen can say which box is wrong before
 * anything is sent.
 *
 * Args:
 *     text: What was typed.
 *
 * Returns:
 *     The number, or `null` when it is not one.
 */
export function parseNumber(text: string): number | null {
  const clean = text.trim().replace(",", ".");
  if (clean === "") {
    return null;
  }
  const value = Number(clean);
  return Number.isFinite(value) ? value : null;
}

/**
 * The same, for a whole number — a version number is not 3.5.
 *
 * Args:
 *     text: What was typed.
 *
 * Returns:
 *     The integer, or `null` when it is not one.
 */
export function parseInteger(text: string): number | null {
  const value = parseNumber(text);
  return value !== null && Number.isInteger(value) ? value : null;
}

/** What a number field says when what is in it is not a number. */
export const NOT_A_NUMBER = "That is not a number — write it as 0.82.";
